import type { ZipDownload } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { extractFilename } from '@vizit/utils';
import * as React from 'react';

/** "Download ready" closes on its own after 6 s (SDD D77). */
const READY_TOAST_TIMEOUT_MS = 6000;
/** "Preparing" stays until the zip arrives, a big zip can take a while. */
const PREPARING_TOAST_TIMEOUT_MS = 10 * 60 * 1000;

/** Title of the failure toasts, callers word the reason under it. */
const DOWNLOAD_FAILED_TITLE = "Couldn't download images";

interface ToastCopy {
  title: string;
  description?: string;
}

interface ZipDownloadRequest {
  /**
   * Gets the zip, or the part starting at `offset` when `partSize` is set. A `null` blob means the API had nothing to
   * put in it (`204`).
   */
  fetchZip: (offset: number) => Promise<ZipDownload>;
  /**
   * Images per part. When set and the first response says there are more images (`totalCount`), the next parts are
   * fetched at offsets `partSize`, `2 * partSize`... and each is saved as its own file (SDD D50, D51).
   */
  partSize?: number;
  /** File name when the API sends none. */
  fallbackFileName: string;
  /** What is bundled, under "Preparing your download", e.g. "Bundling the hero and 4 carousel images for {product}…" */
  preparing: string;
  /** What part 2, 3... bundles, under "Preparing part {part} of {parts}". Falls back to `preparing`. */
  preparingPart?: string;
  /** What was saved, under "Download ready"; `totalCount` and `files` say how a download in parts went. */
  ready: string | ((result: { totalCount: number | null; files: number }) => string);
  /** Why there is no file, under "Nothing to download", when no part had anything to zip (`204`). */
  empty: string;
  /** The failure toast for an error, e.g. by its API error code. Falls back to a generic one. */
  describeError?: (error: unknown) => ToastCopy | undefined;
}

const GENERIC_FAILURE: ToastCopy = {
  title: DOWNLOAD_FAILED_TITLE,
  description: 'Something went wrong. Please try again.',
};

/** Hands a blob to the browser as a download, no new tab. */
function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Downloads a zip from the API with the toasts of the Bulk Download handoff (SDD D77), no modal: "Preparing your
 * download" while the zip is built, then "Download ready" (closes after 6 s), or a failure toast. The caller words what
 * is bundled, the titles are the same for every flow. The file is named from `Vizit-Content-Disposition`. A download
 * too big for one zip comes in parts, one file each, with a "Preparing" toast per part; a part with no originals
 * (`204`) is skipped and the rest still come. Downloads run side by side and keep going when the user moves on to
 * another page, the toasts live at the app root, so only closing the tab stops one. Shared by the PDP download and
 * the collection downloads.
 *
 * @example
 * const { download, isDownloading } = useZipDownload();
 * download({ fetchZip: () => downloadPdpImages({ workspace_id, pdp_id }), ... });
 */
function useZipDownload() {
  const { addToast, dismissToast } = useToast();
  const [downloadCount, setDownloadCount] = React.useState(0);

  const download = React.useCallback(
    async (request: ZipDownloadRequest) => {
      const { fetchZip, partSize, fallbackFileName, preparing, preparingPart, ready, empty, describeError } = request;
      setDownloadCount((count) => count + 1);

      const showPreparing = (title: string, description: string) =>
        addToast({ title, description, level: AlertLevel.Info, timeout: PREPARING_TOAST_TIMEOUT_MS });
      let files = 0;
      const save = ({ blob, contentDisposition }: ZipDownload) => {
        if (!blob) return;
        saveBlob(blob, extractFilename(contentDisposition) ?? fallbackFileName);
        files++;
      };

      let preparingId = showPreparing('Preparing your download', preparing);
      try {
        const first = await fetchZip(0);
        dismissToast(preparingId);
        save(first);

        // The API answers `204` per part, so an empty part says nothing about the others
        const totalCount = first.totalCount ?? null;
        const parts = partSize && totalCount ? Math.ceil(totalCount / partSize) : 1;
        for (let part = 2; part <= parts; part++) {
          preparingId = showPreparing(`Preparing part ${part} of ${parts}`, preparingPart ?? preparing);
          const zip = await fetchZip((part - 1) * (partSize ?? 0));
          dismissToast(preparingId);
          save(zip);
        }

        if (files === 0) {
          addToast({ title: 'Nothing to download', description: empty, level: AlertLevel.Warning });
          return;
        }
        addToast({
          title: 'Download ready',
          description: typeof ready === 'function' ? ready({ totalCount, files }) : ready,
          level: AlertLevel.Success,
          timeout: READY_TOAST_TIMEOUT_MS,
        });
      } catch (error) {
        dismissToast(preparingId);
        addToast({ ...(describeError?.(error) ?? GENERIC_FAILURE), level: AlertLevel.Error });
      } finally {
        setDownloadCount((count) => count - 1);
      }
    },
    [addToast, dismissToast],
  );

  return { download, isDownloading: downloadCount > 0 };
}

export { DOWNLOAD_FAILED_TITLE, useZipDownload };
export type { ToastCopy, ZipDownloadRequest };
